import React, { useCallback, useEffect, useState, useRef } from 'react';
import NProgress from 'react-nprogress';
import { useHistory } from 'react-router-dom';

import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  CloseCircleOutlined,
  MinusCircleOutlined,
  EditOutlined,
  DownloadOutlined,
  ExclamationCircleOutlined,
  DeleteOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { Button, Card, Divider, Modal, Select, Space, Tag, Empty } from 'antd';
import _ from 'lodash';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { Editor, InputMask, Table } from '~/components';
import Loading from '~/components/Loading';
import {
  dataPtBr,
  formatarReal,
  formatModulo,
  preencherTemplate,
  setPageHeader,
  decryptObject,
  stringToFloat,
  maskDocument,
} from '~/helpers/functions';
import { removeItemStorage, setStorage } from '~/helpers/storage';

import { deleteContrato, getContratos, putContrato, downloadRelatorio } from './actions';
import Filtro from './Filtro';
import S from './styles';

const { Option } = Select;
const { confirm } = Modal;

function Home({ location }) {
  const tinyRef = useRef();
  const history = useHistory();

  const [data, setData] = useState([]);
  const [contrato, setContrato] = useState('');
  const [consolidado, setConsolidado] = useState({
    credito: 0,
    honorario: 0,
    mediaHonorarioRecorrente: 0,
  });
  const [totalItens, setTotalItens] = useState(0);
  const [loading, setLoading] = useState({ active: false, text: '' });
  const [filtro, setFiltro] = useState({
    page: 1,
    pageSize: 20,
    empresa: '',
    modulo: '',
    tipo: '',
    chance: '',
    status: '',
  });

  const doGetContratos = async () => {
    await getContratos(filtro)
      .then((response) => {
        let newData = [];

        if (!_.isEmpty(response)) {
          const { resultados, paginacao } = response;

          newData = resultados.map((row) => {
            return {
              ...row,
              camposRow: row.campos.reduce((prev, item) => {
                return {
                  ...prev,
                  [item.nome]: item.valor,
                };
              }, {}),
            };
          });

          setTotalItens(paginacao.total);
          setConsolidado(response.consolidado);
        }

        setData(newData);
        removeItemStorage('contrato', 'session');
        removeItemStorage('novoTemplate', 'session');
      });
  };

  const verificarFiltroRota = () => {
    const searchUrl = queryString.parse(location.search);
    const newFiltro = filtro;

    // TODO: Removido Keycloak - Ajustar caso necessário
    // Remove a querystring da url
    // history.push(location.pathname);
    // Trata a querystring como filtro
    if (searchUrl && searchUrl.f) {
      const filtroUrl = decryptObject(searchUrl.f);
      Object.keys(filtroUrl).forEach((key) => {
        if (newFiltro[key] !== filtroUrl[key]) {
          newFiltro[key] = filtroUrl[key];
        }
      });

      setFiltro(newFiltro);
    }
  };

  const initialRender = useCallback(async () => {
    setLoading({ active: true, text: 'Aguarde enquanto buscamos as Propostas/Contratos' });
    verificarFiltroRota();
    await doGetContratos();
    setLoading({ active: false, text: '' });
  }, [filtro]); // eslint-disable-line

  useEffect(() => {
    initialRender();
  }, [initialRender]);

  const renderTagChance = (value) => {
    switch (value.toLowerCase()) {
      case 'alta':
        return (
          <Tag icon={<ArrowUpOutlined />} color="green">
            Alta
          </Tag>
        );
      case 'media':
        return (
          <Tag icon={<MinusCircleOutlined />} color="blue">
            Média
          </Tag>
        );
      case 'baixa':
        return (
          <Tag icon={<ArrowDownOutlined />} color="red">
            Baixa
          </Tag>
        );
      default:
        return (
          <Tag icon={<MinusCircleOutlined />} color="default">
            -
          </Tag>
        );
    }
  };

  const renderTagStatus = (value) => {
    switch (value.toLowerCase()) {
      case 'andamento':
        return (
          <Tag icon={<ClockCircleOutlined />} color="default">
            Em andamento
          </Tag>
        );
      case 'aceito':
        return (
          <Tag icon={<CheckCircleOutlined />} color="success">
            Aceito
          </Tag>
        );
      case 'recusado':
        return (
          <Tag icon={<CloseCircleOutlined />} color="error">
            Recusado
          </Tag>
        );
      default:
        return (
          <Tag icon={<MinusCircleOutlined />} color="default">
            -
          </Tag>
        );
    }
  };

  const handleSave = async (row) => {
    NProgress.start();

    const newData = [...data];
    const index = newData.findIndex((item) => row.id === item.id);
    const item = newData[index];
    const newRow = {
      ...row,
      credito:
        typeof row.credito === 'string'
          ? parseFloat(
            String(row.credito)
              .replace(/[^a-z0-9,/]/g, '')
              .replace(',', '.')
          )
          : row.credito,
    };
    newData.splice(index, 1, { ...item, ...newRow });
    delete newRow.camposRow;

    await putContrato(newRow).then(async (response) => {
      if (response) {
        await doGetContratos();
      }
    });
    NProgress.done();
  };

  const doAbrirContrato = (record) => {
    setStorage('contrato', record, 'session');
    history.push('/editar/informar-dados');
  };

  const doDownloadTemplate = async (campos, template) => {
    setLoading({ active: true, text: 'Aguarde enquanto o Contrato/Proposta é gerado' });

    await preencherTemplate(campos, template).then(async (response) => {
      await setPageHeader(response).then((novoTemplate) => setContrato(novoTemplate));
    });

    window.setTimeout(() => {
      setLoading({ active: false, text: '' });
      tinyRef.current.imprimirTemplate();
    }, 0);
  };

  const doConfirmarExcluir = (id) => {
    confirm({
      title: `Você tem certeza que deseja excluir o registro?`,
      icon: <ExclamationCircleOutlined />,
      onOk() {
        return new Promise((resolve, reject) => {
          deleteContrato(id).then((response) => {
            if (response) {
              doGetContratos();
              resolve();
            }
            reject();
          });
        });
      },
      onCancel() { },
    });
  };

  const columns = [
    {
      title: 'Empresa',
      dataIndex: 'empresa',
      key: 'empresa',
      width: 370,
      render: (value) => value || '-',
    },
    {
      title: 'CNPJ',
      dataIndex: 'cnpj',
      key: 'modulo',
      width: 150,
      render: (value) => maskDocument(value),
    },
    {
      title: 'Módulo',
      dataIndex: 'modulo',
      key: 'modulo',
      width: 320,
      render: (value) => formatModulo(value),
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo',
      key: 'tipo',
      width: 80,
    },
    {
      title: 'Data',
      dataIndex: 'dataCriacao',
      key: 'dataCriacao',
      align: 'center',
      width: 100,
      render: (value) => dataPtBr(value),
    },
    {
      title: 'Crédito',
      dataIndex: 'credito',
      key: 'credito',
      align: 'right',
      cursor: 'pointer',
      editable: true,
      editableField: (ref, save, toggleEdit) => (
        <InputMask.Currency
          inputRef={ref}
          size="small"
          onKeyPress={(e) => (e.key === 'Enter' ? save() : null)}
          onBlur={toggleEdit}
        />
      ),
      render: (value) => formatarReal(value, true),
    },
    {
      title: 'Honorários (%)',
      dataIndex: ['camposRow', 'HonorarioPercentual'],
      key: 'HonorarioPercentual',
      align: 'center',
      render: (value) => (value ? `${value}%` : '-'),
    },
    {
      title: 'Honorários (R$)',
      dataIndex: 'honorarioValor',
      key: 'honorarioValor',
      align: 'right',
      render: (value, record) =>
        record.camposRow.HonorarioPercentual
          ? formatarReal(
            (stringToFloat(record.camposRow.HonorarioPercentual) * record.credito) / 100,
            true
          )
          : '-',
    },
    {
      title: 'Honorário Recorrente',
      dataIndex: ['camposRow', 'HonorarioRecorrente'],
      key: 'honorarioRecorrente',
      align: 'right',
      render: (value) => formatarReal(value, true) || '-',
    },
    {
      title: 'Recorrência',
      dataIndex: ['camposRow', 'Recorrencia'],
      key: 'recorrencia',
      align: 'center',
      render: (value) => value || '-',
    },
    {
      title: 'Chance',
      dataIndex: 'chance',
      key: 'chance',
      align: 'center',
      width: 100,
      fixed: 'right',
      cursor: 'pointer',
      editable: true,
      editableField: (ref, save, toggleEdit) => (
        <Select ref={ref} size="small" onChange={save} onBlur={toggleEdit}>
          <Option value="Alta">Alta</Option>
          <Option value="Media">Média</Option>
          <Option value="Baixa">Baixa</Option>
        </Select>
      ),
      render: (value) => renderTagChance(value),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      align: 'center',
      width: 145,
      fixed: 'right',
      cursor: 'pointer',
      editable: true,
      editableField: (ref, save, toggleEdit) => (
        <Select ref={ref} size="small" onChange={save} onBlur={toggleEdit}>
          <Option value="Andamento">Em andamento</Option>
          <Option value="Aceito">Aceito</Option>
          <Option value="Recusado">Recusado</Option>
        </Select>
      ),
      render: (value) => renderTagStatus(value),
    },
    {
      width: 120,
      fixed: 'right',
      render: (value, record) => (
        <Space>
          <Button
            type="default"
            icon={<DownloadOutlined />}
            onClick={() => doDownloadTemplate(record.campos, record.template)}
          />

          <Button
            type="primary"
            icon={<DeleteOutlined />}
            onClick={() => doConfirmarExcluir(record.id)}
            danger
          />

          <Button type="primary" icon={<EditOutlined />} onClick={() => doAbrirContrato(record)} />
        </Space>
      ),
    },
  ];

  const propsTable = {
    columns,
    handleSave,
    totalItens,
    dataSource: data,
    setPagination: setFiltro,
    scroll: { x: 2180 },
  };

  return (
    <S.Home>
      <Loading active={loading.active} text={loading.text} />

      {totalItens ? (
        <Space direction="vertical" size="large">
          <Filtro filtro={filtro} setFiltro={setFiltro} />

          {data.length ? (
            <Card
              title="Propostas ou Contratos"
              extra={
                <Space size="middle">
                  <Space direction="vertical">
                    <span style={{ fontSize: 13 }}>
                      <strong>Total de créditos apurado: </strong>
                      {formatarReal(consolidado.credito, true)}
                    </span>
                    <span style={{ fontSize: 13 }}>
                      <strong>Total de Honorários sobre êxito: </strong>
                      {formatarReal(consolidado.honorario, true)}
                    </span>
                    <span style={{ fontSize: 13 }}>
                      <strong>Média mensal de Honorários recorrentes: </strong>
                      {formatarReal(consolidado.mediaHonorarioRecorrente, true)}
                    </span>
                  </Space>

                  <Divider type="vertical" style={{ height: 80 }} />

                  <Button icon={<FileExcelOutlined />} onClick={() => downloadRelatorio(filtro)}>
                    Exportar
                  </Button>
                </Space>
              }
            >
              <Table {...propsTable} />
            </Card>
          ) : (
              <Empty description="Não encontramos Propostas ou Contratos para o seu filtro" />
            )}
        </Space>
      ) : (
          <Empty description="Você não possui Propostas ou Contratos" />
        )}

      <Editor
        className="editor-hide"
        ref={tinyRef}
        content={contrato}
        height="0"
        disabled
        readOnly
      />
    </S.Home>
  );
}

Home.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
    search: PropTypes.string,
  }),
};

Home.defaultProps = {
  location: {
    pathname: '',
    search: '',
  },
};

export default Home;
